import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setWalletModalOpen,
  setActiveTab,
} from "../../store/slices/walletSlice";
import {
  fetchWalletBalanceAction,
  fetchTransactionHistoryAction,
  rechargeWalletAction,
  loadRazorpayScript,
} from "../../store/actions/walletAction";
import {
  truncateToTwoDecimals,
  formatToTwoFractionalDigits,
} from "../../utils/HelperFunctions";
import { WalletTransaction } from "td_server/services";
import "./WalletModal.scss";

interface PresetOption {
  amount: number;
  isPopular?: boolean;
}

const PRESET_AMOUNTS_DOMESTIC: PresetOption[] = [
  { amount: 100 },
  { amount: 200 },
  { amount: 500, isPopular: true },
  { amount: 1000 },
  { amount: 2000 },
  { amount: 5000 },
];

const PRESET_AMOUNTS_OVERSEAS: PresetOption[] = [
  { amount: 5 },
  { amount: 10 },
  { amount: 20, isPopular: true },
  { amount: 50 },
  { amount: 100 },
  { amount: 200 },
];

const WalletModal: React.FC = () => {
  const dispatch: any = useDispatch();
  const {
    balance,
    totalAdded,
    totalSpent,
    transactions,
    isLoading,
    isTransactionsLoading,
    isRecharging,
    isModalOpen,
    activeTab,
  } = useSelector((state: any) => state.wallet);

  const { userInfo } = useSelector((state: any) => state.user);
  const priceConversionState = useSelector(
    (state: any) => state.priceConversion,
  );
  const {
    rate = 1,
    currencySymbols = "₹",
    currencyCode = "INR",
    isOverseas = false,
  } = priceConversionState?.priceConversionRate || {};

  const formatCoins = (val: number | string): string => {
    return Number(val || 0).toLocaleString("en-IN");
  };

  const formatCurrency = (inrVal: number | string): string => {
    const num = Number(inrVal || 0);
    if (!isOverseas) {
      return `₹${num.toLocaleString("en-IN")}`;
    }
    const truncated = truncateToTwoDecimals(num * rate);
    return `${currencySymbols}${formatToTwoFractionalDigits(truncated)}`;
  };

  const getCurrencySymbol = (
    currency?: string,
    fallback: string = "",
  ): string => {
    if (!currency) return fallback || "₹";
    const code = currency.toUpperCase().trim();
    switch (code) {
      case "INR":
        return "₹";
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "AUD":
        return "A$";
      case "CAD":
        return "CA$";
      case "SGD":
        return "S$";
      case "AED":
        return "AED ";
      case "JPY":
        return "¥";
      case "NZD":
        return "NZ$";
      default:
        try {
          const parts = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: code,
          }).formatToParts(0);
          const symbolPart = parts.find((p) => p.type === "currency");
          return symbolPart ? symbolPart.value : fallback || `${code} `;
        } catch (e) {
          return fallback || `${code} `;
        }
    }
  };

  const formatTopupAmount = (txn: WalletTransaction): string => {
    const rawCurrency = ((txn as any)?.currency || "").toUpperCase().trim();
    const rawAmount =
      txn.amount !== undefined && txn.amount !== null
        ? Number(txn.amount)
        : Number(txn.coins || 0);

    if (rawCurrency) {
      const symbol = getCurrencySymbol(
        rawCurrency,
        (txn as any)?.currencySymbols || "",
      );

      if (rawCurrency === "INR") {
        return `${symbol}${rawAmount.toLocaleString("en-IN")}`;
      }

      // For overseas currencies like USD, EUR, etc.
      const formattedNum =
        rawAmount % 1 !== 0
          ? formatToTwoFractionalDigits(truncateToTwoDecimals(rawAmount))
          : rawAmount.toLocaleString("en-US");

      return `${symbol}${formattedNum}`;
    }

    return formatCurrency(txn.amount || txn.coins || 0);
  };

  const coinUnitConverted = formatToTwoFractionalDigits(
    truncateToTwoDecimals(1 * rate),
  );

  const getEffectiveUserId = (): string => {
    let id =
      userInfo?.id ||
      userInfo?._id ||
      userInfo?.userId ||
      userInfo?.user?.id ||
      userInfo?.phoneNumber;
    if (!id) {
      try {
        const stored = localStorage.getItem("userInfoV1");
        if (stored) {
          const parsed = JSON.parse(stored);
          id =
            parsed?.id ||
            parsed?._id ||
            parsed?.userId ||
            parsed?.user?.id ||
            parsed?.phoneNumber;
        }
      } catch (e) {}
    }
    return id || "";
  };

  const effectiveUserId = getEffectiveUserId();

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [inputAmount, setInputAmount] = useState<string>(
    isOverseas ? "5" : "500",
  );
  const [copiedTxnId, setCopiedTxnId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Sync default input when overseas state changes
  useEffect(() => {
    setInputAmount(isOverseas ? "5" : "500");
  }, [isOverseas]);

  const numericAmount = parseFloat(inputAmount) || 0;

  // When overseas: user entered local currency (e.g. $5 USD)
  // Coins to credit = round(numericAmount / rate)
  // When domestic: user entered INR (e.g. ₹500 INR)
  // Coins to credit = round(numericAmount)
  const calculatedCoins = isOverseas
    ? rate > 0
      ? Math.round(numericAmount / rate)
      : 0
    : Math.round(numericAmount);

  // Fetch balance and transactions when modal opens, and ensure conversion rate is loaded
  useEffect(() => {
    if (isModalOpen) {
      if (effectiveUserId) {
        dispatch(fetchWalletBalanceAction(effectiveUserId));
        dispatch(fetchTransactionHistoryAction(effectiveUserId, 10));
      }
      setFeedback(null);
    }
  }, [
    isModalOpen,
    effectiveUserId,
    priceConversionState?.priceConversionRate,
    dispatch,
  ]);

  if (!isModalOpen) return null;

  const handleClose = () => {
    setFeedback(null);
    dispatch(setWalletModalOpen(false));
  };

  const handleTabChange = (tab: number) => {
    if (tab === activeTab) return;
    setSlideDirection(tab > activeTab ? "right" : "left");
    dispatch(setActiveTab(tab));
    setFeedback(null);
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (
      tab === 1 &&
      effectiveUserId &&
      (!transactions || transactions.length === 0)
    ) {
      dispatch(fetchTransactionHistoryAction(effectiveUserId, 10));
    }
  };

  const handlePresetSelect = (val: number) => {
    setInputAmount(String(val));
    setFeedback(null);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawVal = e.target.value;
    if (isOverseas) {
      rawVal = rawVal.replace(/[^0-9.]/g, "");
      const parts = rawVal.split(".");
      if (parts.length > 2) {
        rawVal = parts[0] + "." + parts.slice(1).join("");
      }
      if (parts[1] && parts[1].length > 2) {
        rawVal = parts[0] + "." + parts[1].slice(0, 2);
      }
      const num = parseFloat(rawVal) || 0;
      if (num > 10000) return;
    } else {
      rawVal = rawVal.replace(/[^0-9]/g, "");
      const num = parseInt(rawVal, 10) || 0;
      if (num > 100000) return;
    }
    setInputAmount(rawVal);
    setFeedback(null);
  };

  const handleRecharge = async () => {
    const res = await loadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const activeUserId = getEffectiveUserId();
    if (!activeUserId) {
      setFeedback({
        type: "error",
        message: "Please log in to recharge your Temple Dekho wallet.",
      });
      return;
    }

    if (!numericAmount || numericAmount <= 0 || calculatedCoins <= 0) {
      setFeedback({
        type: "error",
        message: `Please enter a valid recharge amount (minimum ${
          isOverseas ? `${currencySymbols}1` : "₹1"
        }).`,
      });
      return;
    }

    setFeedback(null);

    // Similar to placeOrder in ReviewCartScreen:
    // Base amount in Indian Rupees (Coins):
    const orderValue = calculatedCoins;
    const indRupees = orderValue;

    let payableAmount: number = 0;
    let orderValueOv: any = null;

    if (!isOverseas) {
      payableAmount = Number(Number(orderValue));
      orderValueOv = null;
    } else {
      payableAmount = numericAmount;
      orderValueOv = payableAmount;
    }

    // Beside amount, send amount in indRupees as well
    dispatch(
      rechargeWalletAction(
        payableAmount,
        indRupees,
        activeUserId,
        userInfo,
        isOverseas,
        rate,
        currencyCode,
        currencySymbols,
        orderValueOv,
        (res: any) => {
          setFeedback({
            type: "success",
            message: `Congratulations! Successfully added ${formatCoins(
              calculatedCoins,
            )} Coins to your wallet.`,
          });
          setInputAmount(isOverseas ? "5" : "500");
        },
        (errMsg: string) => {
          setFeedback({
            type: "error",
            message:
              errMsg || "Payment could not be completed. Please try again.",
          });
        },
      ),
    );
  };

  const handleCopy = (text: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedTxnId(text);
      setTimeout(() => setCopiedTxnId(null), 2000);
    }
  };

  return (
    <div
      className="walletModalOverlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="walletModalTitle"
    >
      <div
        className="walletModalContainer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mobileDragHandle" />

        {/* Modal Header */}
        <div className="modalHeader">
          <div className="headerTitleGroup">
            <div className="walletHeaderIcon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
                <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
              </svg>
            </div>
            <div className="titleWrap">
              <h3 id="walletModalTitle">Temple Dekho Wallet</h3>
            </div>
          </div>
          <button
            type="button"
            className="closeBtn"
            onClick={handleClose}
            aria-label="Close wallet modal"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Hero Balance Banner */}
        <div className="balanceBanner">
          <div className="bannerTop">
            <span className="bannerLabel">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Available Balance
            </span>
          </div>

          <div className="bannerMain">
            <span className="coinSymbol">
              {isOverseas ? currencySymbols : "₹"}
            </span>
            <span className="coinValue">
              {isLoading && balance === 0 ? "..." : formatCoins(balance)}
            </span>
            <span className="coinText">Coins</span>
            <span className="inrEquivalent">
              {isOverseas ? (
                <>
                  ≈ {formatCurrency(balance)}{" "}
                  <small style={{ opacity: 0.85 }}>({currencyCode})</small>
                </>
              ) : (
                `≈ ₹${formatCoins(balance)}`
              )}
            </span>
          </div>

          <div className="bannerFooter">
            <div className="statItem">
              <span>Added:</span>
              <b>
                {formatCurrency(totalAdded)}
                {isOverseas && (
                  <span className="footerCoinHint">
                    {" "}
                    (+{formatCoins(totalAdded)} Coins)
                  </span>
                )}
              </b>
            </div>
            <div className="statItem">
              <span>Spent:</span>
              <b>
                {formatCurrency(totalSpent)}
                {isOverseas && (
                  <span className="footerCoinHint">
                    {" "}
                    ({formatCoins(totalSpent)} Coins)
                  </span>
                )}
              </b>
            </div>
          </div>
        </div>

        {/* Segmented Tab Navigation with Sliding Indicator */}
        <div className="tabNavigation">
          <div
            className={`tabIndicator ${activeTab === 1 ? "tab1" : "tab0"}`}
            aria-hidden="true"
          />
          <button
            type="button"
            className={`tabButton ${activeTab === 0 ? "active" : ""}`}
            onClick={() => handleTabChange(0)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" x2="22" y1="10" y2="10" />
            </svg>
            <span>Add Coins</span>
          </button>
          <button
            type="button"
            className={`tabButton ${activeTab === 1 ? "active" : ""}`}
            onClick={() => handleTabChange(1)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" x2="8" y1="13" y2="13" />
              <line x1="16" x2="8" y1="17" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <span>Passbook & History</span>
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="modalBody" ref={modalBodyRef}>
          {/* Feedback Alert Toast */}
          {feedback && (
            <div className={`alertBanner ${feedback.type}`}>
              {feedback.type === "success" ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" x2="12" y1="8" y2="12" />
                  <line x1="12" x2="12.01" y1="16" y2="16" />
                </svg>
              )}
              <span>{feedback.message}</span>
            </div>
          )}

          {/* Smooth Sliding Tab Panels Container */}
          <div className="tabPanelsContainer">
            {/* TAB 0: RECHARGE / ADD COINS */}
            <div
              className={`tabPanel ${activeTab === 0 ? "active" : "hidden"} ${
                slideDirection === "left" ? "fromLeft" : "fromRight"
              }`}
              aria-hidden={activeTab !== 0}
            >
              <div className="rechargeSection">
                <div className="sectionHeading">
                  <span>Select Recharge Amount</span>
                  <span className="popularBadge">⚡ Instant Topup</span>
                </div>

                {/* Preset Chips Grid */}
                <div className="presetChipsGrid">
                  {(isOverseas
                    ? PRESET_AMOUNTS_OVERSEAS
                    : PRESET_AMOUNTS_DOMESTIC
                  ).map((preset) => {
                    const presetCoins = isOverseas
                      ? rate > 0
                        ? Math.round(preset.amount / rate)
                        : 0
                      : preset.amount;
                    const isSelected = numericAmount === preset.amount;

                    return (
                      <button
                        key={preset.amount}
                        type="button"
                        className={`presetChip ${isSelected ? "selected" : ""}`}
                        onClick={() => handlePresetSelect(preset.amount)}
                      >
                        {preset.isPopular && (
                          <span className="chipBadge">POPULAR</span>
                        )}
                        <span className="chipAmount">
                          {isOverseas
                            ? `${currencySymbols}${preset.amount}`
                            : `₹${preset.amount}`}
                        </span>
                        <span className="chipCoins">
                          +{formatCoins(presetCoins)} Coins
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Custom Input Card */}
                <div className="customAmountCard">
                  <div className="inputLabel">
                    {isOverseas
                      ? `Or Enter Custom Amount (${currencyCode})`
                      : "Or Enter Custom Amount (INR)"}
                  </div>
                  <div className="amountInputRow">
                    <span className="currencySign">
                      {isOverseas ? currencySymbols : "₹"}
                    </span>
                    <input
                      type="text"
                      inputMode={isOverseas ? "decimal" : "numeric"}
                      className="amountInput"
                      placeholder={isOverseas ? "5" : "500"}
                      value={inputAmount}
                      onChange={handleAmountChange}
                      maxLength={isOverseas ? 8 : 6}
                    />
                    <span className="coinsPill">
                      <span className="goldCoinIcon">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="M14.8 9A2 2 0 0 0 13 8h-2a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4h-2a2 2 0 0 1-1.8-1" />
                          <path d="M12 6v2m0 8v2" />
                        </svg>
                      </span>
                      <span>{formatCoins(calculatedCoins)} Coins</span>
                    </span>
                  </div>
                  {numericAmount > 0 && (
                    <div className="dynamicConversionHint">
                      <span>Gets You:</span>
                      <b>+{formatCoins(calculatedCoins)} Coins</b>
                      <span className="rateNote">
                        {isOverseas
                          ? `(1 Coin = ${currencySymbols}${coinUnitConverted} ${currencyCode})`
                          : "(1 INR = 1 Coin)"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Order Summary Breakdown Card */}
                <div className="orderSummaryCard">
                  <div className="summaryRow">
                    <span>Recharge Value</span>
                    <span>
                      {isOverseas
                        ? `${currencySymbols}${formatToTwoFractionalDigits(
                            truncateToTwoDecimals(numericAmount),
                          )}`
                        : `₹${formatCoins(calculatedCoins)}`}
                    </span>
                  </div>
                  <div className="summaryRow">
                    <span>Coins Credited</span>
                    <span className="highlightText">
                      +{formatCoins(calculatedCoins)} Coins
                    </span>
                  </div>
                  <div className="summaryRow">
                    <span>Conversion Rate</span>
                    <span>
                      {isOverseas
                        ? `1 Coin = ${currencySymbols}${coinUnitConverted} ${currencyCode}`
                        : "1 INR = 1 Coin"}
                    </span>
                  </div>
                  <div className="summaryRow totalRow">
                    <span>Total Payable Amount</span>
                    <span className="payableAmountWrap">
                      <b>
                        {isOverseas
                          ? `${currencySymbols}${formatToTwoFractionalDigits(
                              truncateToTwoDecimals(numericAmount),
                            )}`
                          : `₹${formatCoins(calculatedCoins)}`}
                      </b>
                      {isOverseas && (
                        <span className="payableCurrencyTag">
                          {currencyCode}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Primary Recharge Button */}
                <button
                  type="button"
                  className="rechargeBtn"
                  disabled={
                    isRecharging ||
                    !numericAmount ||
                    numericAmount <= 0 ||
                    calculatedCoins <= 0
                  }
                  onClick={handleRecharge}
                >
                  {isRecharging ? (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ animation: "spin 1s linear infinite" }}
                      >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                      Processing Secure Payment...
                    </>
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      {isOverseas
                        ? `Proceed to Pay ${currencySymbols}${formatToTwoFractionalDigits(
                            truncateToTwoDecimals(numericAmount),
                          )}`
                        : `Proceed to Pay ₹${formatCoins(calculatedCoins)}`}
                    </>
                  )}
                </button>

                {/* Trust Footer */}
                <div className="trustFooter">
                  <span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    256-Bit SSL Encrypted
                  </span>
                  <span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                    Instant Coin Credit
                  </span>
                  <span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                    </svg>
                    Razorpay Secured
                  </span>
                </div>
              </div>
            </div>

            {/* TAB 1: PASSBOOK & HISTORY */}
            <div
              className={`tabPanel ${activeTab === 1 ? "active" : "hidden"} ${
                slideDirection === "right" ? "fromRight" : "fromLeft"
              }`}
              aria-hidden={activeTab !== 1}
            >
              <div className="historySection">
                <div className="historyControls">
                  <span className="historyCount">
                    Recent Ledger ({transactions?.length || 0})
                  </span>
                  <button
                    type="button"
                    className={`refreshBtn ${isTransactionsLoading ? "isRefreshing" : ""}`}
                    onClick={() =>
                      effectiveUserId &&
                      dispatch(
                        fetchTransactionHistoryAction(effectiveUserId, 10),
                      )
                    }
                    title="Refresh passbook"
                    disabled={isTransactionsLoading}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={isTransactionsLoading ? "spinIcon" : ""}
                    >
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                      <path d="M8 16H3v5" />
                    </svg>
                    {isTransactionsLoading ? "Refreshing..." : "Refresh"}
                  </button>
                </div>

                {isTransactionsLoading &&
                (!transactions || transactions.length === 0) ? (
                  <div className="txnSkeletonList">
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item} className="txnSkeletonCard">
                        <div className="skeletonIcon shimmer" />
                        <div className="skeletonDetails">
                          <div className="skeletonLine short shimmer" />
                          <div className="skeletonLine long shimmer" />
                        </div>
                        <div className="skeletonAmount shimmer" />
                      </div>
                    ))}
                  </div>
                ) : transactions && transactions.length > 0 ? (
                  <div className="txnList">
                    {transactions.map((txn: WalletTransaction) => {
                      const isCredit =
                        txn.type === "credit" ||
                        (txn.type as string)?.toLowerCase() === "credit";
                      const isSuccess = txn.status === "success";
                      const isPending = txn.status === "pending";
                      const formattedDate = txn.createdAt
                        ? new Date(txn.createdAt).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "Recently";

                      const displayTxnId = txn.id || txn.razorpayOrderId || "";

                      return (
                        <div key={txn.id || displayTxnId} className="txnCard">
                          <div
                            className={`txnTypeIcon ${
                              isCredit ? "credit" : "debit"
                            }`}
                          >
                            {isCredit ? (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="12" x2="12" y1="5" y2="19" />
                                <polyline points="19 12 12 19 5 12" />
                              </svg>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <line x1="12" x2="12" y1="19" y2="5" />
                                <polyline points="5 12 12 5 19 12" />
                              </svg>
                            )}
                          </div>

                          <div className="txnDetails">
                            <div className="txnTitleRow">
                              <span className="txnTitle">
                                {isCredit
                                  ? "Wallet Top-up"
                                  : "Voice Chat with Guruji Payment"}
                              </span>
                              <span
                                className={`txnStatusChip ${
                                  isSuccess
                                    ? "success"
                                    : isPending
                                      ? "pending"
                                      : "failed"
                                }`}
                              >
                                {txn.status || "success"}
                              </span>
                            </div>

                            <div className="txnMetaRow">
                              <span>{formattedDate}</span>
                              {displayTxnId && (
                                <span
                                  className="refId"
                                  title="Click to copy Transaction Reference"
                                  onClick={() => handleCopy(displayTxnId)}
                                >
                                  Ref: {displayTxnId.slice(0, 8)}...
                                  {copiedTxnId === displayTxnId ? (
                                    <span
                                      style={{
                                        color: "#10b981",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ✓ Copied
                                    </span>
                                  ) : (
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <rect
                                        width="14"
                                        height="14"
                                        x="8"
                                        y="8"
                                        rx="2"
                                        ry="2"
                                      />
                                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                    </svg>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="txnAmountCol">
                            <div
                              className={`coinsDelta ${
                                isCredit ? "positive" : "negative"
                              }`}
                            >
                              {isCredit ? "+" : "-"}
                              {formatCoins(txn.coins || txn.amount || 0)} Coins
                            </div>
                            <div className="inrAmount">
                              {isCredit || (txn as any)?.source === "recharge"
                                ? formatTopupAmount(txn)
                                : formatCurrency(txn.amount || txn.coins || 0)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="emptyState">
                    <div className="emptyIconWrap">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="14" x="2" y="5" rx="2" />
                        <line x1="2" x2="22" y1="10" y2="10" />
                      </svg>
                    </div>
                    <h4>No Transactions Yet</h4>
                    <p>
                      Your wallet passbook will automatically record all top-ups
                      and Puja bookings here.
                    </p>
                    <button
                      type="button"
                      className="emptyActionBtn"
                      onClick={() => handleTabChange(0)}
                    >
                      + Add Coins Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletModal;
